import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Snackbar,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import type { AlertColor } from "@mui/material";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
// Raw MUI loaders (LinearProgress/CircularProgress/Backdrop) are confined to
// shared/ui/progress by the loading-UI boundary guard, so use the sanctioned
// shared primitives instead of importing LinearProgress here.
import { OperationProgressIndicator, ProgressMeter } from "../progress";
import {
    usePluginUpdateStore,
    useUpdateBannerMode,
    type UpdateBannerMode,
} from "../../stores/usePluginUpdateStore";
import { usePluginUpdatePolling } from "../../lib/hooks/usePluginUpdatePolling";
import { humanizeFailureReason } from "../../lib/utils/humanizeFailureReason";

/**
 * Compact, non-blocking banner covering the whole plugin self-update lifecycle:
 * update-available → downloading → verifying → ready-to-apply, plus the
 * install-failed branch. Mounted once at the app shell (AppContent); it renders
 * nothing unless the store reports a visible mode, so it is safe to keep
 * permanently mounted. On mount it reads the cached update state once; outside
 * Revit (no gRPC runtime) the read resolves to a hidden state and the app keeps
 * working.
 */
const SEVERITY_BY_MODE: Record<UpdateBannerMode, AlertColor> = {
    hidden: "info",
    "update-available": "info",
    downloading: "info",
    verifying: "info",
    "ready-to-apply": "success",
    "install-failed": "warning",
};

const CLOSE_LABEL_BY_MODE: Partial<Record<UpdateBannerMode, string>> = {
    "update-available": "Dismiss update notification",
    "ready-to-apply": "Dismiss update ready notification",
    "install-failed": "Dismiss update failure notification",
};

interface BannerBodyProps {
    mode: UpdateBannerMode;
    installedVersion: string;
    latestVersion: string;
    stagedVersion: string;
    releaseNotesUrl: string;
    downloadProgressPercent: number;
    failureReason: string;
    errorMessage: string;
    canRetry: boolean;
    autoDownloadEnabled: boolean;
    onDownload: () => void;
    onRetry: () => void;
}

/** Pure per-mode banner content. Kept out of the wrapper to bound both sizes. */
const BannerBody: React.FC<BannerBodyProps> = ({
    mode,
    installedVersion,
    latestVersion,
    stagedVersion,
    releaseNotesUrl,
    downloadProgressPercent,
    failureReason,
    errorMessage,
    canRetry,
    autoDownloadEnabled,
    onDownload,
    onRetry,
}) => {
    const caption = { display: "block", color: "text.secondary" } as const;

    switch (mode) {
        case "update-available":
            return (
                <>
                    <AlertTitle
                        sx={{ fontSize: "0.8rem", fontWeight: 700, mb: 0.25 }}
                    >
                        Plugin update available
                    </AlertTitle>
                    <Typography variant="caption" sx={caption}>
                        A newer PipeEX plugin is available. Installed{" "}
                        <strong>{installedVersion || "unknown"}</strong> &rarr;
                        latest <strong>{latestVersion}</strong>.
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {releaseNotesUrl ? (
                            <Button
                                component="a"
                                href={releaseNotesUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                variant="text"
                                data-testid="plugin-update-release-notes"
                            >
                                Release notes
                            </Button>
                        ) : null}
                        {autoDownloadEnabled ? null : (
                            <Button
                                size="small"
                                variant="contained"
                                onClick={onDownload}
                                data-testid="plugin-update-download"
                            >
                                Download update
                            </Button>
                        )}
                    </Stack>
                </>
            );
        case "downloading":
            return (
                <>
                    <AlertTitle
                        sx={{ fontSize: "0.8rem", fontWeight: 700, mb: 0.25 }}
                    >
                        Updating PipeEX
                    </AlertTitle>
                    <Typography variant="caption" sx={caption}>
                        {`Downloading update ${latestVersion}… ${downloadProgressPercent}%`}
                    </Typography>
                    <Box data-testid="plugin-update-progress" sx={{ mt: 1 }}>
                        <ProgressMeter
                            value={downloadProgressPercent}
                            tone="info"
                            ariaLabel={`Downloading update ${latestVersion}`}
                        />
                    </Box>
                </>
            );
        case "verifying":
            return (
                <>
                    <AlertTitle
                        sx={{ fontSize: "0.8rem", fontWeight: 700, mb: 0.25 }}
                    >
                        Updating PipeEX
                    </AlertTitle>
                    <Typography variant="caption" sx={caption}>
                        Verifying update&hellip;
                    </Typography>
                    <Box
                        data-testid="plugin-update-progress"
                        sx={{ mt: 1, display: "flex", alignItems: "center" }}
                    >
                        {/* Indeterminate stage — no measurable percentage, so a status
                spinner rather than a valued meter. */}
                        <OperationProgressIndicator ariaLabel="Verifying update" />
                    </Box>
                </>
            );
        case "ready-to-apply":
            return (
                <Box data-testid="plugin-update-ready">
                    <AlertTitle
                        sx={{ fontSize: "0.8rem", fontWeight: 700, mb: 0.25 }}
                    >
                        Update ready
                    </AlertTitle>
                    <Typography variant="caption" sx={caption}>
                        PipeEX {stagedVersion} is installed. Restart Revit to
                        apply.
                    </Typography>
                </Box>
            );
        case "install-failed":
            return (
                <>
                    <AlertTitle
                        sx={{ fontSize: "0.8rem", fontWeight: 700, mb: 0.25 }}
                    >
                        Update failed
                    </AlertTitle>
                    <Typography variant="caption" sx={caption}>
                        {humanizeFailureReason(failureReason, errorMessage)}
                    </Typography>
                    {canRetry ? (
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={onRetry}
                                data-testid="plugin-update-retry"
                            >
                                Retry
                            </Button>
                        </Stack>
                    ) : null}
                </>
            );
        default:
            return null;
    }
};

const PluginUpdatePrompt: React.FC = () => {
    const theme = useTheme();
    const ensureChecked = usePluginUpdateStore((state) => state.ensureChecked);
    const startDownload = usePluginUpdateStore((state) => state.startDownload);
    const dismissCurrent = usePluginUpdateStore(
        (state) => state.dismissCurrent,
    );
    const dismissStaged = usePluginUpdateStore((state) => state.dismissStaged);
    const dismissFailure = usePluginUpdateStore(
        (state) => state.dismissFailure,
    );
    const installedVersion = usePluginUpdateStore(
        (state) => state.installedVersion,
    );
    const latestVersion = usePluginUpdateStore((state) => state.latestVersion);
    const stagedVersion = usePluginUpdateStore((state) => state.stagedVersion);
    const releaseNotesUrl = usePluginUpdateStore(
        (state) => state.releaseNotesUrl,
    );
    const downloadProgressPercent = usePluginUpdateStore(
        (state) => state.downloadProgressPercent,
    );
    const failureReason = usePluginUpdateStore((state) => state.failureReason);
    const errorMessage = usePluginUpdateStore((state) => state.errorMessage);
    const canRetry = usePluginUpdateStore((state) => state.canRetry);
    const autoDownloadEnabled = usePluginUpdateStore(
        (state) => state.autoDownloadEnabled,
    );
    const storeToastVersion = usePluginUpdateStore(
        (state) => state.postRestartToastVersion,
    );
    const acknowledgeInstalledVersion = usePluginUpdateStore(
        (state) => state.acknowledgeInstalledVersion,
    );
    const mode = useUpdateBannerMode();

    // Own the toast lifetime locally: the store raises postRestartToastVersion,
    // we capture it and immediately acknowledge (records the version + clears the
    // store flag) so the toast fires exactly once, then autohide dismisses it.
    const [toastVersion, setToastVersion] = useState("");

    // Poll the plugin while an install is active (no-op in every other mode).
    usePluginUpdatePolling();

    useEffect(() => {
        void ensureChecked();
    }, [ensureChecked]);

    useEffect(() => {
        if (storeToastVersion) {
            setToastVersion(storeToastVersion);
            acknowledgeInstalledVersion();
        }
    }, [storeToastVersion, acknowledgeInstalledVersion]);

    const handleDownload = useCallback(() => {
        void startDownload();
    }, [startDownload]);

    const handleToastClose = useCallback(
        (_event?: unknown, reason?: string) => {
            if (reason === "clickaway") {
                return;
            }
            setToastVersion("");
        },
        [],
    );

    const onClose =
        mode === "update-available"
            ? dismissCurrent
            : mode === "ready-to-apply"
              ? dismissStaged
              : mode === "install-failed"
                ? dismissFailure
                : undefined;

    return (
        <>
            {mode === "hidden" ? null : (
                <Box
                    data-testid="plugin-update-prompt"
                    sx={{
                        position: "fixed",
                        right: theme.spacing(1.5),
                        bottom: theme.spacing(7),
                        zIndex: theme.zIndex.snackbar,
                        maxWidth: 360,
                        width: "calc(100% - 24px)",
                    }}
                >
                    <Alert
                        severity={SEVERITY_BY_MODE[mode]}
                        variant="outlined"
                        icon={<SystemUpdateAltIcon fontSize="small" />}
                        onClose={onClose}
                        slotProps={
                            onClose
                                ? {
                                      closeButton: {
                                          "aria-label":
                                              CLOSE_LABEL_BY_MODE[mode],
                                      },
                                  }
                                : undefined
                        }
                        sx={{
                            alignItems: "flex-start",
                            backgroundColor: theme.palette.background.paper,
                            boxShadow: theme.shadows[6],
                            py: 0.75,
                            "& .MuiAlert-message": { width: "100%" },
                        }}
                    >
                        <BannerBody
                            mode={mode}
                            installedVersion={installedVersion}
                            latestVersion={latestVersion}
                            stagedVersion={stagedVersion}
                            releaseNotesUrl={releaseNotesUrl}
                            downloadProgressPercent={downloadProgressPercent}
                            failureReason={failureReason}
                            errorMessage={errorMessage}
                            canRetry={canRetry}
                            autoDownloadEnabled={autoDownloadEnabled}
                            onDownload={handleDownload}
                            onRetry={handleDownload}
                        />
                    </Alert>
                </Box>
            )}

            {/* Post-restart success toast — shown independently of the banner, so it
          still appears once the plugin is up to date again after a restart. */}
            <Snackbar
                open={!!toastVersion}
                autoHideDuration={6000}
                onClose={handleToastClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity="success"
                    variant="filled"
                    onClose={handleToastClose}
                    data-testid="plugin-update-toast"
                    sx={{ width: "100%" }}
                >
                    Updated to {toastVersion}
                </Alert>
            </Snackbar>
        </>
    );
};

export default PluginUpdatePrompt;
